// src/lib/api.js
// ─────────────────────────────────────────────────────────────
// HTTP klient – náhrada za Supabase SDK
// Komunikuje s Express backendem (server/index.js) na Railway
// ─────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Heslo se NIKDY neukládá do JS bundlu – čte se z runtime sessionStorage
// (nastaveno po úspěšném přihlášení na serveru)
function getAdminPassword() {
  return sessionStorage.getItem('jic_admin_pass') || '';
}

// Fetch s timeoutem – zabrání nekonečnému čekání při výpadku serveru
function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

// ── Upsert draft (průběžné ukládání) ─────────────────────────
export async function upsertApplication(data) {
  try {
    const res = await fetch(`${API_URL}/api/applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.ok;
  } catch (err) {
    console.error('upsertApplication error:', err);
    return false;
  }
}

// ── Nahrání souboru ───────────────────────────────────────────
export async function uploadFile(sessionId, file, slot) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/upload/${sessionId}/${slot}`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) return null;
    const { path } = await res.json();
    return path;
  } catch (err) {
    console.error('uploadFile error:', err);
    return null;
  }
}

// ── Ověření admin hesla na serveru ───────────────────────────
export async function verifyAdminPassword(password) {
  try {
    const res = await fetch(`${API_URL}/api/applications`, {
      headers: { 'x-admin-password': password },
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Načtení všech přihlášek (admin) ──────────────────────────
// Vrací pole přihlášek nebo null při chybě serveru/sítě
// (null = chyba; [] = prázdná DB – důležitý rozdíl pro UI)
export async function fetchAllApplications() {
  try {
    const res = await fetchWithTimeout(`${API_URL}/api/applications`, {
      headers: { 'x-admin-password': getAdminPassword() },
    });
    if (!res.ok) return null;
    return res.json();
  } catch (err) {
    console.error('fetchAllApplications error:', err);
    return null;
  }
}

// ── Finální odeslání – uloží do DB, vygeneruje PDF, pošle emaily
// questions a sections se posílají na server, aby mohl sestavit PDF
export async function submitApplication(data, questions, sections) {
  try {
    const res = await fetchWithTimeout(`${API_URL}/api/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data, questions, sections }),
    }, 60000); // 60 s – PDF generování může chvíli trvat
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return true;
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Server neodpovídá. Zkontrolujte připojení a zkuste to znovu.');
    }
    console.error('submitApplication error:', err);
    throw err;
  }
}

// ── Smazání přihlášky (admin) ─────────────────────────────────
export async function deleteApplication(id) {
  try {
    const res = await fetch(`${API_URL}/api/applications/${id}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': getAdminPassword() },
    });
    return res.ok;
  } catch (err) {
    console.error('deleteApplication error:', err);
    return false;
  }
}

// ── URL pro stažení přílohy ───────────────────────────────────
// Vrací URL + header objekt; caller musí fetch volat ručně s headerem
// (heslo se NESMÍ posílat jako query param – bylo by v URL logu)
export function getAttachmentUrl(filePath) {
  return `${API_URL}/api/files/${filePath}`;
}

export function getAdminHeaders() {
  return { 'x-admin-password': getAdminPassword() };
}
